import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { initiatePayment } from '@/utils/razorpay';
import { getUser } from '@/utils/auth';
import { 
  Check, Flame, TrendingUp, Rocket, Trophy, Crown, 
  MessageCircle, Phone, Users, Building2, ArrowRight,
  Building, Home as HomeIcon, ChevronRight, Loader2
} from 'lucide-react';

const PartnerSubscriptions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('agent');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [agentTiers, setAgentTiers] = useState([]);
  const [developerTiers, setDeveloperTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappAddon, setWhatsappAddon] = useState({ price: 999, period: 'month' });
  const [activeBanner, setActiveBanner] = useState(null); // active partner_subscription promo

  const iconMap = {
    Flame: Flame,
    TrendingUp: TrendingUp,
    Rocket: Rocket,
    Trophy: Trophy,
    Crown: Crown,
  };

  const colorMap = {
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-500', badge: 'bg-emerald-500', gradient: 'from-emerald-500 to-teal-500' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-500', badge: 'bg-blue-500', gradient: 'from-blue-500 to-indigo-500' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-500', badge: 'bg-purple-500', gradient: 'from-purple-500 to-violet-500' },
    red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-500', badge: 'bg-red-500', gradient: 'from-red-500 to-rose-500' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-500', badge: 'bg-amber-500', gradient: 'from-amber-500 to-orange-500' },
  };

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const API_URL = '';
      
      const [agentRes, devRes, waAddonRes, bannerRes] = await Promise.all([
        fetch(`${API_URL}/api/subscriptions/tiers/agent`),
        fetch(`${API_URL}/api/subscriptions/tiers/developer`),
        fetch(`${API_URL}/api/subscription-pricing/whatsapp-addon`),
        fetch(`${API_URL}/api/coupons/banners?business_unit=partner_subscription`),
      ]);
      
      const agentData = await agentRes.json();
      const devData = await devRes.json();
      const waData = await waAddonRes.json();
      const bannerData = bannerRes.ok ? await bannerRes.json() : { banners: [] };
      
      setAgentTiers(agentData.tiers || []);
      setDeveloperTiers(devData.tiers || []);
      if (waData.addon) {
        setWhatsappAddon(waData.addon);
      }
      // Surface the first active percentage-discount promo for price display
      const promos = (bannerData.banners || []).filter(
        (b) => b.discount_type === 'percentage' && b.discount_value > 0
      );
      setActiveBanner(promos[0] || null);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Compute discounted price if a percentage promo is active
  const getPromoPrice = (originalPrice) => {
    if (!activeBanner || originalPrice == null) return null;
    const tenure = activeBanner.subscription_tenure;
    if (tenure && tenure !== billingCycle) return null; // tenure mismatch
    return Math.round(originalPrice * (1 - activeBanner.discount_value / 100));
  };

  const getPrice = (tier) => {
    if (tier.is_enterprise) return null;
    const pricing = tier.pricing || {};
    const key = billingCycle === 'yearly' ? 'annual' : billingCycle;
    return pricing[key] ?? pricing.monthly;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Custom';
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getPeriodText = () => {
    if (billingCycle === 'monthly') return '/ month';
    if (billingCycle === 'six_month') return '/ 6 months';
    return '/ year';
  };

  const [payingTier, setPayingTier] = useState(null);

  const handleGetStarted = () => {
    window.location.href = '/api/partner-portal';
  };

  const handleSubscribe = async (tier) => {
    const user = getUser();
    if (!user) {
      toast.error('Please login or register first to subscribe');
      navigate('/register');
      return;
    }

    if (tier.is_enterprise) {
      window.open('mailto:enterprise@zenstudios.in?subject=Enterprise Plan Inquiry', '_blank');
      return;
    }

    setPayingTier(tier.tier);
    try {
      await initiatePayment({
        userId: user.user_id || user.agent_id || user.developer_id,
        userType: activeTab,
        tier: tier.tier,
        billingCycle: billingCycle,
        addons: [],
        userName: user.name || user.full_name,
        userEmail: user.email,
        userPhone: user.phone,
        onSuccess: (data) => {
          toast.success(`Successfully subscribed to ${tier.name}! Your dashboard is now ready.`);
          setTimeout(() => {
            window.location.href = '/api/partner-portal';
          }, 2000);
        },
        onFailure: (data) => {
          toast.error(data.error || 'Payment failed. Please try again.');
        },
        onDismiss: () => {
          toast.info('Payment cancelled');
        },
      });
    } catch (err) {
      if (err.message !== 'Payment cancelled by user') {
        toast.error(err.message || 'Payment failed. Please try again.');
      }
    } finally {
      setPayingTier(null);
    }
  };

  const tiers = activeTab === 'agent' ? agentTiers : developerTiers;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="partner-subscriptions-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 text-white py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Partner with ZenStudios
          </h1>
          <p className="text-lg text-indigo-200 mb-8 max-w-2xl mx-auto">
            Join India's fastest-growing real estate platform. Get premium tools, 
            qualified leads, and dedicated support to grow your business.
          </p>
          
          {/* Partner Type Toggle */}
          <div className="inline-flex bg-white/10 backdrop-blur-sm rounded-2xl p-1.5">
            <button
              onClick={() => setActiveTab('agent')}
              className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                activeTab === 'agent'
                  ? 'bg-white text-indigo-900 shadow-lg'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              <Users className="w-5 h-5" />
              For Agents
            </button>
            <button
              onClick={() => setActiveTab('developer')}
              className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                activeTab === 'developer'
                  ? 'bg-white text-indigo-900 shadow-lg'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              <Building2 className="w-5 h-5" />
              For Developers
            </button>
          </div>
        </div>
      </div>

      {/* Active Promotion Banner */}
      {activeBanner && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 py-2.5 px-4 text-center"
             data-testid="partner-promo-banner">
          <span className="text-sm font-semibold text-amber-800">
            Limited time: {activeBanner.discount_value}% off — use code{' '}
            <span className="font-mono bg-amber-100 border border-amber-300 rounded px-2 py-0.5 text-amber-900 text-xs">
              {activeBanner.code}
            </span>
            {activeBanner.valid_until && (
              <span className="text-xs text-amber-600 ml-2">· Ends {activeBanner.valid_until.slice(0,10)}</span>
            )}
          </span>
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-slate-200 py-4 z-10">
        <div className="max-w-6xl mx-auto px-4 flex justify-center">
          <div className="bg-slate-100 p-1 rounded-xl inline-flex">
            {[
              { key: 'monthly', label: 'Monthly' },
              { key: 'six_month', label: '6 Months', savings: '10%' },
              { key: 'annual', label: 'Annual', savings: '15%' },
            ].map((cycle) => (
              <button
                key={cycle.key}
                onClick={() => setBillingCycle(cycle.key)}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  billingCycle === cycle.key
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {cycle.label}
                {cycle.savings && (
                  <span className="ml-1.5 text-xs text-emerald-600 font-semibold">
                    Save {cycle.savings}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {tiers.map((tier) => {
            const colors = colorMap[tier.color] || colorMap.emerald;
            const Icon = iconMap[tier.icon] || Flame;
            const price = getPrice(tier);

            return (
              <div
                key={tier.tier}
                data-testid={`tier-card-${tier.tier}`}
                className={`bg-white rounded-2xl border-2 p-5 relative flex flex-col transition-all hover:shadow-lg ${
                  tier.popular ? `${colors.border} shadow-md` : 'border-slate-200'
                }`}
              >
                {tier.popular && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r ${colors.gradient} text-white text-xs font-semibold rounded-full shadow-md`}>
                    Most Popular
                  </div>
                )}

                {/* Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-xl ${colors.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-800">{tier.name}</h3>
                  </div>
                </div>

                {/* Tagline */}
                <p className="text-xs text-slate-500 mb-4 leading-relaxed">{tier.tagline}</p>

                {/* Price */}
                <div className="mb-4">
                  {tier.is_enterprise ? (
                    <div>
                      <span className="text-2xl font-bold text-slate-800">Custom</span>
                      <p className="text-xs text-slate-500 mt-1">
                        {activeTab === 'developer' ? 'Starting from ₹1,25,000/mo' : 'Tailored pricing'}
                      </p>
                    </div>
                  ) : (() => {
                    const promoPrice = getPromoPrice(price);
                    return (
                      <>
                        {promoPrice != null && (
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs line-through text-slate-400">{formatPrice(price)}</span>
                            <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                              {activeBanner.discount_value}% off
                            </span>
                          </div>
                        )}
                        <span className={`text-3xl font-bold ${promoPrice != null ? 'text-amber-600' : 'text-slate-800'}`}>
                          {formatPrice(promoPrice ?? price)}
                        </span>
                        <span className="text-slate-500 text-sm ml-1">{getPeriodText()}</span>
                        {promoPrice != null && (
                          <p className="text-xs text-amber-600 mt-1 font-medium"
                             data-testid={`promo-savings-${tier.tier}`}>
                            Save {formatPrice(price - promoPrice)} — use code <span className="font-mono font-bold">{activeBanner.code}</span>
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Limits Info */}
                {activeTab === 'agent' && tier.listing_limits && !tier.is_enterprise && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Rental Listings:</span>
                      <span className="font-semibold text-slate-800">{tier.listing_limits.rental}</span>
                    </div>
                    {tier.listing_limits.sale > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Sale Listings:</span>
                        <span className="font-semibold text-slate-800">{tier.listing_limits.sale}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-600">Validity:</span>
                      <span className="font-semibold text-slate-800">{tier.validity_days} days</span>
                    </div>
                  </div>
                )}

                {activeTab === 'developer' && tier.project_limit && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Building className="w-3.5 h-3.5 text-slate-500" />
                        <span className="text-slate-600">Projects:</span>
                      </div>
                      <span className="font-semibold text-slate-800">
                        {tier.project_limit === -1 ? 'Unlimited' : `Up to ${tier.project_limit}`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Features */}
                <ul className="space-y-2 mb-5 flex-grow">
                  {tier.features.slice(0, 5).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                      <Check className={`w-4 h-4 ${colors.text} flex-shrink-0 mt-0.5`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {tier.features.length > 5 && (
                    <li className="text-xs text-slate-400 pl-6">
                      +{tier.features.length - 5} more features
                    </li>
                  )}
                </ul>

                {/* CTA Button */}
                {tier.is_enterprise ? (
                  <Button
                    className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md"
                    onClick={() => window.open('mailto:enterprise@zenstudios.in?subject=Enterprise Plan Inquiry', '_blank')}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Contact Sales
                  </Button>
                ) : (
                  <Button
                    className={`w-full rounded-xl ${
                      tier.popular
                        ? `bg-gradient-to-r ${colors.gradient} hover:opacity-90 shadow-md`
                        : 'bg-slate-800 hover:bg-slate-700'
                    } text-white`}
                    onClick={() => handleSubscribe(tier)}
                    disabled={payingTier === tier.tier}
                    data-testid={`subscribe-${tier.tier}-btn`}
                  >
                    {payingTier === tier.tier ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <>Subscribe Now <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* WhatsApp Add-on */}
        <div className="mt-12 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-6">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-14 h-14 rounded-2xl bg-green-500 flex items-center justify-center flex-shrink-0 shadow-lg">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <div className="flex-grow">
              <h3 className="font-bold text-xl text-slate-800">{whatsappAddon.name || 'WhatsApp Lead Alerts Add-on'}</h3>
              <p className="text-slate-600 mt-1">Get instant lead notifications directly on WhatsApp</p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {(whatsappAddon.features || ['Instant Notifications', 'Auto Lead Summary', 'Click-to-Respond', 'Missed Lead Alerts']).map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-green-500" />
                    {feature}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Included free with Dominate & Rule packages • Available as add-on for other plans
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-3xl font-bold text-slate-800">₹{whatsappAddon.price?.toLocaleString('en-IN') || '999'}</div>
              <div className="text-sm text-slate-500">/ {whatsappAddon.period || 'month'}</div>
              <Button className="mt-3 bg-green-500 hover:bg-green-600 text-white rounded-xl shadow-md" onClick={handleGetStarted}>
                Add to Plan
              </Button>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-3">Ready to grow your business?</h2>
          <p className="text-slate-600 mb-6">Join thousands of partners already succeeding on ZenStudios</p>
          <Button 
            size="lg" 
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-8 shadow-lg"
            onClick={handleGetStarted}
          >
            Start Your Free Trial
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PartnerSubscriptions;
